import React, { useEffect, useState } from 'react';
import {
    Modal,
    Button,
    Form,
    Table,
    Spinner,
    Alert,
    Pagination,
    Row,
    Col,
    Badge,
    ButtonGroup,
    Card,
} from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import 'react-toastify/dist/ReactToastify.css';

const OwnerWithdraws = () => {
    const navigate = useNavigate();

    const [requests, setRequests] = useState([]);
    const [filteredRequests, setFilteredRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Filter by status (server-side)
    const [statusFilter, setStatusFilter] = useState('pending');

    // Summary (from API - only for pending)
    const [pendingSummary, setPendingSummary] = useState({ count: 0, amount: 0 });

    // Pagination (server-side)
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    // Search (client-side on current page)
    const [searchQuery, setSearchQuery] = useState('');
    const [filterField, setFilterField] = useState('ownerName');

    // Modal states
    const [showActionModal, setShowActionModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [actionStatus, setActionStatus] = useState('approved');
    const [actionRemark, setActionRemark] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const API_BASE_URL = 'https://varahibackend.varahiselfdrivecars.com/api/admin';

    // ---------- Helpers ----------
    const getStatusBadgeVariant = (status) => {
        switch (status) {
            case 'approved': return 'success';
            case 'pending': return 'warning';
            case 'rejected':
            case 'reject': return 'danger';
            case 'reversed': return 'secondary';
            default: return 'secondary';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'approved': return 'fa-check-circle';
            case 'pending': return 'fa-clock';
            case 'rejected':
            case 'reject': return 'fa-times-circle';
            case 'reversed': return 'fa-undo';
            default: return 'fa-circle';
        }
    };

    const formatStatus = (status) => {
        if (!status) return '-';
        if (status === 'reject') return 'rejected';
        return status;
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '₹0';
        return `₹${Number(value).toLocaleString('en-IN')}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        try {
            return new Date(dateStr).toLocaleString('en-IN', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
            });
        } catch {
            return '—';
        }
    };

    // ---------- API ----------
    const fetchRequests = async (page = currentPage) => {
        setLoading(true);
        setError('');
        try {
            const url = `${API_BASE_URL}/withdraw-requests?status=${statusFilter}&page=${page}&limit=${limit}`;
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const result = await response.json();
            if (result.success) {
                const data = Array.isArray(result.requests) ? result.requests : [];
                setRequests(data);
                setFilteredRequests(data);
                if (result.pendingSummary) {
                    setPendingSummary(result.pendingSummary);
                }
                if (result.pagination) {
                    setCurrentPage(result.pagination.currentPage || 1);
                    setTotalPages(result.pagination.totalPages || 1);
                    setTotalItems(result.pagination.totalItems || 0);
                }
                setSearchQuery('');
            } else {
                throw new Error(result.message || 'Failed to fetch withdraw requests');
            }
        } catch (err) {
            setError(err.message);
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const updateWithdrawRequest = async (requestId, status, remark) => {
        try {
            const response = await fetch(`${API_BASE_URL}/withdraw-requests/${requestId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status, remark }),
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const result = await response.json();
            if (result.success) {
                toast.success(`Withdraw request ${formatStatus(status)} successfully`);
                return true;
            } else {
                throw new Error(result.message || 'Failed to update withdraw request');
            }
        } catch (err) {
            toast.error(err.message);
            return false;
        }
    };

    const openActionModal = (request, presetStatus) => {
        setSelectedRequest(request);
        setActionStatus(presetStatus || 'approved');
        setActionRemark('');
        setShowActionModal(true);
    };

    const handleActionSubmit = async () => {
        if (!selectedRequest) return;
        setSubmitting(true);
        const ok = await updateWithdrawRequest(selectedRequest._id, actionStatus, actionRemark);
        setSubmitting(false);
        if (ok) {
            setShowActionModal(false);
            setSelectedRequest(null);
            await fetchRequests(currentPage);
        }
    };

    const handleSearch = (query) => {
        setSearchQuery(query);
        if (!query.trim()) {
            setFilteredRequests(requests);
            return;
        }
        const q = query.toLowerCase();
        const filtered = requests.filter((req) => {
            const owner = req.ownerId || {};
            switch (filterField) {
                case 'ownerName':
                    return (owner.fullName || '').toLowerCase().includes(q);
                case 'email':
                    return (owner.email || '').toLowerCase().includes(q);
                case 'mobileNumber':
                    return (owner.mobileNumber || '').toLowerCase().includes(q);
                case 'remark':
                    return (req.remark || '').toLowerCase().includes(q);
                case 'status':
                    return (req.status || '').toLowerCase().includes(q);
                default:
                    return false;
            }
        });
        setFilteredRequests(filtered);
    };

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages || page === currentPage) return;
        setCurrentPage(page);
        fetchRequests(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // ---------- Pagination: 1, ..., n-1, n, n+1, ..., last ----------
    const renderPagination = () => {
        if (!totalPages || totalPages < 1) return null;

        const items = [];
        const pagesToShow = new Set();
        pagesToShow.add(1);
        pagesToShow.add(totalPages);
        if (currentPage > 1) pagesToShow.add(currentPage - 1);
        pagesToShow.add(currentPage);
        if (currentPage < totalPages) pagesToShow.add(currentPage + 1);

        const sorted = Array.from(pagesToShow).filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);

        let last = 0;
        sorted.forEach((page) => {
            if (page - last > 1) {
                items.push(<Pagination.Ellipsis key={`ellipsis-${page}`} disabled />);
            }
            items.push(
                <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                >
                    {page}
                </Pagination.Item>
            );
            last = page;
        });

        return (
            <Pagination className="mt-3 justify-content-center flex-wrap withdraw-pagination">
                <Pagination.First
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(1)}
                />
                <Pagination.Prev
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                />
                {items}
                <Pagination.Next
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                />
                <Pagination.Last
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(totalPages)}
                />
            </Pagination>
        );
    };

    // ---------- Export ----------
    const exportToExcel = () => {
        try {
            if (!filteredRequests.length) {
                toast.info('No requests to export');
                return;
            }
            toast.info('Preparing Excel file...', { autoClose: 2000 });
            const exportData = filteredRequests.map((req) => ({
                'Request ID': req._id,
                'Owner ID': req.ownerId?._id || '-',
                'Owner Name': req.ownerId?.fullName || '-',
                Email: req.ownerId?.email || '-',
                'Mobile Number': req.ownerId?.mobileNumber || '-',
                Type: req.type,
                Amount: req.amount,
                Status: formatStatus(req.status),
                Remark: req.remark || '-',
                'Wallet Balance': req.ownerWallet?.walletBalance ?? '-',
                'Available Balance': req.ownerWallet?.availableBalance ?? '-',
                'Total Withdrawn': req.ownerWallet?.totalWithdrawn ?? '-',
                'Processed At': req.processedAt ? new Date(req.processedAt).toLocaleString() : '-',
                'Created At': new Date(req.createdAt).toLocaleString(),
                'Updated At': new Date(req.updatedAt).toLocaleString(),
            }));
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'WithdrawRequests');
            worksheet['!cols'] = [
                { wch: 25 }, { wch: 25 }, { wch: 20 }, { wch: 28 },
                { wch: 15 }, { wch: 12 }, { wch: 10 }, { wch: 12 },
                { wch: 25 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
                { wch: 20 }, { wch: 20 }, { wch: 20 },
            ];
            XLSX.writeFile(workbook, `WithdrawRequests_${new Date().toISOString().slice(0, 19)}.xlsx`);
            toast.success('Excel file downloaded successfully!', { autoClose: 2000 });
        } catch (err) {
            console.error(err);
            toast.error('Failed to generate Excel file');
        }
    };

    useEffect(() => {
        setCurrentPage(1);
        fetchRequests(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    // ---------- Summary cards config ----------
    const summaryCards = [
        {
            label: 'Pending Requests',
            value: pendingSummary.count ?? 0,
            icon: 'fa-hourglass-half',
            gradient: 'linear-gradient(135deg,#fbbf24,#b45309)',
            isCurrency: false,
        },
        {
            label: 'Pending Amount',
            value: pendingSummary.amount ?? 0,
            icon: 'fa-coins',
            gradient: 'linear-gradient(135deg,#f87171,#b91c1c)',
            isCurrency: true,
        },
        {
            label: 'Total Requests (This View)',
            value: totalItems,
            icon: 'fa-list-ol',
            gradient: 'linear-gradient(135deg,#22d3ee,#0e7490)',
            isCurrency: false,
        },
    ];

    return (
        <div className="container-fluid p-2 p-md-3 withdraw-wrapper">
            <ToastContainer position="top-right" autoClose={2000} />

            <style>{`
        .withdraw-wrapper {
          background: linear-gradient(135deg,#f8fafc 0%,#eef2ff 100%);
          min-height: 100vh;
        }
        .withdraw-wrapper .page-title {
          font-weight: 700;
          letter-spacing: .3px;
          background: linear-gradient(90deg,#4f46e5,#06b6d4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .withdraw-summary-card {
          border: none !important;
          border-radius: 14px !important;
          color: #fff;
          transition: transform .18s ease, box-shadow .18s ease;
          overflow: hidden;
        }
        .withdraw-summary-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 22px rgba(0,0,0,.18);
        }
        .withdraw-summary-card .summary-icon {
          font-size: 1.6rem;
          opacity: .85;
        }
        .withdraw-summary-card .summary-label {
          font-size: .72rem;
          text-transform: uppercase;
          letter-spacing: .08em;
          opacity: .9;
          font-weight: 600;
        }
        .withdraw-summary-card .summary-value {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 0;
        }
        .withdraw-table thead th {
          background: linear-gradient(90deg,#1e293b,#334155) !important;
          color: #fff !important;
          font-size: .8rem;
          text-transform: uppercase;
          letter-spacing: .04em;
          vertical-align: middle;
          white-space: nowrap;
        }
        .withdraw-table tbody tr:hover {
          background: #f1f5ff !important;
        }
        .withdraw-table td {
          vertical-align: middle;
          font-size: .88rem;
        }
        .withdraw-pagination .page-item.active .page-link {
          background: linear-gradient(90deg,#4f46e5,#06b6d4);
          border-color: transparent;
          color: #fff;
        }
        .withdraw-pagination .page-link {
          color: #4f46e5;
          border-radius: 8px !important;
          margin: 0 2px;
        }
        .withdraw-pagination .page-link:hover {
          background: #eef2ff;
        }
        .withdraw-table-card {
          border: none !important;
          border-radius: 14px !important;
          box-shadow: 0 4px 14px rgba(15,23,42,.08);
          overflow: hidden;
        }
        .withdraw-table-card .card-header {
          background: linear-gradient(90deg,#1e293b,#334155);
          color: #fff;
          font-weight: 600;
          border: none;
        }
        .filter-bar {
          background: #f8fafc;
          border-radius: 12px;
          padding: .75rem;
        }
        .mobile-request-card {
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          margin-bottom: .75rem;
          background: #fff;
          overflow: hidden;
        }
        .mobile-request-card .mobile-card-header {
          padding: .6rem .75rem;
          background: linear-gradient(90deg,#1e293b,#334155);
          color: #fff;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .owner-avatar-sm {
          width: 40px; height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg,#4f46e5,#06b6d4);
          color: #fff;
          display: flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 1rem;
        }
        @media (max-width: 575.98px) {
          .withdraw-summary-card .summary-value { font-size: 1.2rem; }
          .withdraw-wrapper { padding: .5rem !important; }
          .page-title { font-size: 1.15rem; }
        }
      `}</style>

            {/* ---------- Header ---------- */}
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <Button
                        variant="light"
                        size="sm"
                        className="border shadow-sm"
                        onClick={() => navigate(-1)}
                    >
                        <i className="fas fa-arrow-left me-1"></i>Back
                    </Button>
                    <h3 className="mb-0 page-title">
                        <i className="fas fa-hand-holding-usd me-2"></i>
                        Owner Withdraw Requests
                    </h3>
                </div>
                <div className="d-flex gap-2">
                    <Button variant="outline-primary" size="sm" onClick={() => fetchRequests(currentPage)}>
                        <i className="fas fa-sync-alt me-2"></i>Refresh
                    </Button>
                    <Button variant="success" size="sm" onClick={exportToExcel}>
                        <i className="fas fa-file-excel me-2"></i>Export
                    </Button>
                </div>
            </div>

            {/* ---------- Summary Cards ---------- */}
            <Row className="mb-3 g-3">
                {summaryCards.map((card) => (
                    <Col xs={12} sm={6} md={4} key={card.label}>
                        <Card
                            className="withdraw-summary-card h-100"
                            style={{ background: card.gradient }}
                        >
                            <Card.Body className="d-flex flex-column justify-content-between">
                                <div className="d-flex justify-content-between align-items-start">
                                    <span className="summary-label">{card.label}</span>
                                    <i className={`fas ${card.icon} summary-icon`}></i>
                                </div>
                                <p className="summary-value mt-2">
                                    {card.isCurrency ? formatCurrency(card.value) : card.value}
                                </p>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* ---------- Filters ---------- */}
            <Card className="withdraw-table-card mb-3">
                <Card.Body className="py-3">
                    <div className="filter-bar">
                        <Row className="g-2">
                            <Col xs={12} md={3}>
                                <Form.Select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="pending">⏳ Pending</option>
                                    <option value="approved">✅ Approved</option>
                                    <option value="reject">❌ Reject</option>
                                </Form.Select>
                            </Col>
                            <Col xs={12} md={3}>
                                <Form.Select
                                    value={filterField}
                                    onChange={(e) => setFilterField(e.target.value)}
                                >
                                    <option value="ownerName">🔍 Search by Owner Name</option>
                                    <option value="email">🔍 Search by Email</option>
                                    <option value="mobileNumber">🔍 Search by Mobile Number</option>
                                    <option value="remark">🔍 Search by Remark</option>
                                    <option value="status">🔍 Search by Status</option>
                                </Form.Select>
                            </Col>
                            <Col xs={12} md={4}>
                                <Form.Control
                                    type="text"
                                    placeholder="Search on current page..."
                                    value={searchQuery}
                                    onChange={(e) => handleSearch(e.target.value)}
                                />
                            </Col>
                            <Col xs={12} md={2}>
                                <Button
                                    variant="outline-secondary"
                                    className="w-100"
                                    onClick={() => handleSearch('')}
                                >
                                    <i className="fas fa-times me-1"></i>Clear
                                </Button>
                            </Col>
                        </Row>
                    </div>
                </Card.Body>
            </Card>

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Loading withdraw requests...</p>
                </div>
            ) : error ? (
                <Alert variant="danger" className="shadow-sm">
                    <i className="fas fa-exclamation-triangle me-2"></i>{error}
                </Alert>
            ) : (
                <>
                    {/* ---------- Desktop Table ---------- */}
                    <Card className="withdraw-table-card d-none d-lg-block">
                        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <span>
                                <i className="fas fa-list-alt me-2"></i>
                                Withdraw Requests
                                <Badge bg="light" text="dark" pill className="ms-2">
                                    {filteredRequests.length}
                                </Badge>
                            </span>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table bordered hover striped className="withdraw-table align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th className="text-center">S.NO</th>
                                            <th>Owner</th>
                                            <th>Contact</th>
                                            <th>Amount</th>
                                            <th>Status</th>
                                            <th>Remark</th>
                                            <th>Wallet Snapshot</th>
                                            <th>Requested At</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredRequests.length === 0 ? (
                                            <tr>
                                                <td colSpan="9" className="text-center py-4 text-muted">
                                                    <i className="fas fa-inbox fa-2x d-block mb-2"></i>
                                                    No withdraw requests found
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredRequests.map((req, index) => (
                                                <tr key={req._id}>
                                                    <td className="text-center">
                                                        <Badge bg="light" text="dark" pill>
                                                            {(currentPage - 1) * limit + index + 1}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="owner-avatar-sm">
                                                                {(req.ownerId?.fullName || '?').charAt(0).toUpperCase()}
                                                            </div>
                                                            <div>
                                                                <div className="fw-semibold">{req.ownerId?.fullName || '-'}</div>
                                                                <div className="small text-muted">
                                                                    {req.ownerId?._id?.slice(-8) || '-'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="small">
                                                            <i className="fas fa-envelope text-primary me-1"></i>
                                                            {req.ownerId?.email || '-'}
                                                        </div>
                                                        <div className="small text-muted">
                                                            <i className="fas fa-phone text-success me-1"></i>
                                                            {req.ownerId?.mobileNumber || '-'}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="fw-bold text-danger">
                                                            {formatCurrency(req.amount)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <Badge
                                                            bg={getStatusBadgeVariant(req.status)}
                                                            className="text-capitalize d-inline-flex align-items-center gap-1"
                                                        >
                                                            <i className={`fas ${getStatusIcon(req.status)}`}></i>
                                                            {formatStatus(req.status)}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-muted small">{req.remark || '—'}</td>
                                                    <td className="small">
                                                        {req.ownerWallet ? (
                                                            <>
                                                                <div>
                                                                    <i className="fas fa-wallet text-primary me-1"></i>
                                                                    <strong>Bal:</strong> {formatCurrency(req.ownerWallet.walletBalance)}
                                                                </div>
                                                                <div>
                                                                    <i className="fas fa-hand-holding-usd text-info me-1"></i>
                                                                    <strong>Avail:</strong> {formatCurrency(req.ownerWallet.availableBalance)}
                                                                </div>
                                                                <div>
                                                                    <i className="fas fa-money-bill-wave text-danger me-1"></i>
                                                                    <strong>Withdrawn:</strong> {formatCurrency(req.ownerWallet.totalWithdrawn)}
                                                                </div>
                                                            </>
                                                        ) : '—'}
                                                    </td>
                                                    <td className="small text-muted">
                                                        <i className="fas fa-clock me-1"></i>
                                                        {formatDate(req.createdAt)}
                                                    </td>
                                                    <td className="text-center">
                                                        {req.status === 'pending' ? (
                                                            <ButtonGroup size="sm">
                                                                <Button
                                                                    variant="outline-success"
                                                                    onClick={() => openActionModal(req, 'approved')}
                                                                >
                                                                    <i className="fas fa-check me-1"></i>Approve
                                                                </Button>
                                                                <Button
                                                                    variant="outline-danger"
                                                                    onClick={() => openActionModal(req, 'reject')}
                                                                >
                                                                    <i className="fas fa-times me-1"></i>Reject
                                                                </Button>
                                                            </ButtonGroup>
                                                        ) : (
                                                            <Button
                                                                variant="outline-secondary"
                                                                size="sm"
                                                                onClick={() => openActionModal(req, req.status)}
                                                            >
                                                                <i className="fas fa-edit me-1"></i>Update
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ---------- Mobile Card View ---------- */}
                    <div className="d-lg-none">
                        {filteredRequests.length === 0 ? (
                            <Card className="withdraw-table-card">
                                <Card.Body className="text-center py-4 text-muted">
                                    <i className="fas fa-inbox fa-2x d-block mb-2"></i>
                                    No withdraw requests found
                                </Card.Body>
                            </Card>
                        ) : (
                            filteredRequests.map((req, index) => (
                                <Card key={req._id} className="mobile-request-card shadow-sm">
                                    <div className="mobile-card-header">
                                        <span className="small">
                                            #{(currentPage - 1) * limit + index + 1}
                                        </span>
                                        <Badge
                                            bg={getStatusBadgeVariant(req.status)}
                                            className="text-capitalize d-inline-flex align-items-center gap-1"
                                        >
                                            <i className={`fas ${getStatusIcon(req.status)}`}></i>
                                            {formatStatus(req.status)}
                                        </Badge>
                                    </div>
                                    <Card.Body className="p-3">
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <div className="owner-avatar-sm">
                                                {(req.ownerId?.fullName || '?').charAt(0).toUpperCase()}
                                            </div>
                                            <div className="flex-grow-1">
                                                <div className="fw-semibold">{req.ownerId?.fullName || '-'}</div>
                                                <div className="small text-muted">
                                                    <i className="fas fa-envelope me-1"></i>
                                                    {req.ownerId?.email || '-'}
                                                </div>
                                                <div className="small text-muted">
                                                    <i className="fas fa-phone me-1"></i>
                                                    {req.ownerId?.mobileNumber || '-'}
                                                </div>
                                            </div>
                                            <div className="text-end">
                                                <div className="small text-muted text-uppercase">Amount</div>
                                                <div className="fw-bold text-danger">
                                                    {formatCurrency(req.amount)}
                                                </div>
                                            </div>
                                        </div>

                                        {req.remark && (
                                            <div className="small text-muted mb-2">
                                                <i className="fas fa-comment-alt me-1"></i>
                                                {req.remark}
                                            </div>
                                        )}

                                        {req.ownerWallet && (
                                            <div className="small p-2 bg-light rounded mb-2">
                                                <Row className="g-1">
                                                    <Col xs={4}>
                                                        <div className="text-muted text-uppercase" style={{ fontSize: '.65rem' }}>Bal</div>
                                                        <div className="fw-semibold">{formatCurrency(req.ownerWallet.walletBalance)}</div>
                                                    </Col>
                                                    <Col xs={4}>
                                                        <div className="text-muted text-uppercase" style={{ fontSize: '.65rem' }}>Avail</div>
                                                        <div className="fw-semibold">{formatCurrency(req.ownerWallet.availableBalance)}</div>
                                                    </Col>
                                                    <Col xs={4}>
                                                        <div className="text-muted text-uppercase" style={{ fontSize: '.65rem' }}>Withdrawn</div>
                                                        <div className="fw-semibold">{formatCurrency(req.ownerWallet.totalWithdrawn)}</div>
                                                    </Col>
                                                </Row>
                                            </div>
                                        )}

                                        <div className="small text-muted mb-2">
                                            <i className="fas fa-clock me-1"></i>
                                            {formatDate(req.createdAt)}
                                        </div>

                                        <div className="d-flex justify-content-end">
                                            {req.status === 'pending' ? (
                                                <ButtonGroup size="sm">
                                                    <Button
                                                        variant="success"
                                                        onClick={() => openActionModal(req, 'approved')}
                                                    >
                                                        <i className="fas fa-check me-1"></i>Approve
                                                    </Button>
                                                    <Button
                                                        variant="danger"
                                                        onClick={() => openActionModal(req, 'reject')}
                                                    >
                                                        <i className="fas fa-times me-1"></i>Reject
                                                    </Button>
                                                </ButtonGroup>
                                            ) : (
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => openActionModal(req, req.status)}
                                                >
                                                    <i className="fas fa-edit me-1"></i>Update
                                                </Button>
                                            )}
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))
                        )}
                    </div>

                    {renderPagination()}
                </>
            )}

            {/* ---------- Action Modal ---------- */}
            <Modal
                show={showActionModal}
                onHide={() => setShowActionModal(false)}
                centered
            >
                <Modal.Header
                    closeButton
                    style={{ background: 'linear-gradient(90deg,#1e293b,#334155)', color: '#fff' }}
                >
                    <Modal.Title>
                        <i className="fas fa-hand-holding-usd me-2"></i>
                        Withdraw Request Action
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedRequest && (
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label className="small text-uppercase text-muted fw-semibold">
                                    Owner
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly
                                    value={`${selectedRequest.ownerId?.fullName || '-'} (${selectedRequest.ownerId?.email || '-'})`}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label className="small text-uppercase text-muted fw-semibold">
                                    Amount
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    readOnly
                                    value={formatCurrency(selectedRequest.amount)}
                                    className="fw-bold text-danger"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label className="small text-uppercase text-muted fw-semibold">
                                    Current Status
                                </Form.Label>
                                <div>
                                    <Badge
                                        bg={getStatusBadgeVariant(selectedRequest.status)}
                                        className="text-capitalize d-inline-flex align-items-center gap-1"
                                    >
                                        <i className={`fas ${getStatusIcon(selectedRequest.status)}`}></i>
                                        {formatStatus(selectedRequest.status)}
                                    </Badge>
                                </div>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label className="small text-uppercase text-muted fw-semibold">
                                    Action
                                </Form.Label>
                                <Form.Select
                                    value={actionStatus}
                                    onChange={(e) => setActionStatus(e.target.value)}
                                >
                                    <option value="approved">✅ Approved</option>
                                    <option value="reject">❌ Reject</option>
                                    <option value="pending">⏳ Pending</option>
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label className="small text-uppercase text-muted fw-semibold">
                                    Remark
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="e.g. Paid by cash"
                                    value={actionRemark}
                                    onChange={(e) => setActionRemark(e.target.value)}
                                />
                            </Form.Group>
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowActionModal(false)}
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleActionSubmit}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Submitting...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-paper-plane me-1"></i>Submit
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default OwnerWithdraws;